@if (stepper.isOpen()) {
  <div
    class="fixed inset-0 z-[500] flex flex-col overflow-hidden bg-talos-bg"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="'PROJECT_STEPPER.ARIA.DIALOG' | translate: { step: stepper.currentStep(), total: stepper.steps.length, label: stepper.stepLabel(stepper.currentStep()), project: stepper.project()?.projectName ?? ('PROJECT_STEPPER.WORKFLOW' | translate) }"
  >
    <!-- Screen-reader live region for step changes -->
    <span class="sr-only" aria-live="polite" aria-atomic="true">
      {{ 'PROJECT_STEPPER.ARIA.STEP_LIVE' | translate: { step: stepper.currentStep(), total: stepper.steps.length, label: stepper.stepLabel(stepper.currentStep()) } }}
    </span>

    <!-- Global header bar (steps 1 & 2 only) -->
    @if (stepper.currentStep() <= 2) {
    <div class="flex items-center justify-between px-4 py-2 bg-[var(--talos-bg)] shrink-0 z-10" style="border-bottom:1px solid rgb(var(--talos-accent-rgb) / 0.22)">
      <div class="flex items-center gap-2.5">
        <span class="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded-full text-[10px] font-bold tabular-nums text-talos-accent"
          style="background:rgb(var(--talos-accent-rgb) / 0.07)">
          {{ stepper.currentStep() }}/{{ stepper.steps.length }}
        </span>
        <span class="text-xs font-bold uppercase tracking-[0.12em] text-talos-ink">
          {{ stepper.stepLabel(stepper.currentStep()) }}
        </span>
      </div>
      <div class="flex items-center gap-3 min-w-0">
        @if (stepper.project()?.projectName) {
          <div class="flex items-center gap-1.5 min-w-0">
            <lucide-icon [img]="FolderOpenIcon" [size]="13" class="shrink-0" color="var(--talos-accent)" />
            <span class="text-xs font-medium truncate max-w-[220px]" style="color:rgb(var(--talos-ink-rgb) / 0.6)">
              {{ stepper.project()!.projectName }}
            </span>
          </div>
        }
        <app-button variant="ghost" [icon]="XIcon" [iconOnly]="true" size="sm" [tooltip]="'COMMON.CLOSE' | translate" (clicked)="onClose()" />
      </div>
    </div>
    }

    <!-- Step content -->
    <div class="flex-1 min-h-0 flex flex-col overflow-hidden">

      @if (stepper.hydrationLoading()) {
        <app-loading-spinner size="lg" [fill]="true" [title]="'PROJECT_STEPPER.LOADING.PROJECT_TITLE' | translate" [message]="'PROJECT_STEPPER.LOADING.PROJECT_MESSAGE' | translate" />
      }

      <!-- Steps 1 & 2: standard scrollable layout -->
      @if (!stepper.hydrationLoading() && (stepper.currentStep() === 1 || stepper.currentStep() === 2)) {
        <div class="flex-1 overflow-y-auto">
          <div class="max-w-5xl mx-auto px-6 py-8">
            <!-- Step heading -->
            <div class="mb-10">
              <div class="flex items-center gap-2 mb-4">
                <span class="text-[9px] font-bold uppercase tracking-[0.22em] text-talos-accent">// {{ 'PROJECT_STEPPER.HEADER.STEP_OF' | translate: { step: stepper.currentStep(), total: stepper.steps.length } }}</span>
                <span class="inline-block w-px h-3" style="background:rgb(var(--talos-accent-rgb) / 0.2)"></span>
                <span class="text-[9px] font-bold uppercase tracking-[0.16em]" style="color:rgb(var(--talos-ink-rgb) / 0.35)">{{ 'PROJECT_STEPPER.WORKFLOW' | translate }}</span>
              </div>
              <h2 class="text-[1.75rem] font-bold text-talos-ink leading-tight mb-2.5">
                @switch (stepper.currentStep()) {
                  @case (1) { {{ 'PROJECT_STEPPER.STEP1.TITLE' | translate }} }
                  @case (2) { {{ 'PROJECT_STEPPER.STEP2.TITLE' | translate }} }
                }
              </h2>
              <p class="text-sm leading-relaxed max-w-md" style="color:rgb(var(--talos-ink-rgb) / 0.48)">
                @switch (stepper.currentStep()) {
                  @case (1) { {{ 'PROJECT_STEPPER.STEP1.DESCRIPTION' | translate }} }
                  @case (2) { {{ 'PROJECT_STEPPER.STEP2.DESCRIPTION' | translate }} }
                }
              </p>
            </div>

            @switch (stepper.currentStep()) {
              @case (1) { <app-step-detection-scope /> }
              @case (2) { <app-step-upload-plan /> }
            }
          </div>
        </div>
      }

      <!-- Step 3: full-height PDF assignment viewer -->
      @if (!stepper.hydrationLoading() && stepper.currentStep() === 3) {
        @defer (when stepper.currentStep() === 3; prefetch on idle) {
          <app-step-assign-floors class="flex flex-col flex-1 min-h-0" />
        } @loading {
          <app-loading-spinner size="lg" [fill]="true" [title]="'PROJECT_STEPPER.LOADING.FLOOR_VIEWER_TITLE' | translate" [message]="'PROJECT_STEPPER.LOADING.FLOOR_VIEWER_MESSAGE' | translate" />
        }
      }

      <!-- Step 4: full-height results view (same layout as step 3) -->
      @if (!stepper.hydrationLoading() && stepper.currentStep() === 4) {
        @defer (when stepper.currentStep() === 4; prefetch on idle) {
          <app-step-results class="flex flex-col flex-1 min-h-0" />
        } @loading {
          <app-loading-spinner size="lg" [fill]="true" [title]="'PROJECT_STEPPER.LOADING.RESULTS_TITLE' | translate" [message]="'PROJECT_STEPPER.LOADING.RESULTS_MESSAGE' | translate" />
        }
      }

    </div>

    <app-confirm-modal />

    <!-- Onboarding coachmark tour -->
    <app-stepper-tour #tour [currentStep]="stepper.currentStep()" />

    <!-- Floating navigation dock -->
    <div class="pointer-events-none fixed inset-x-0 bottom-3 z-[520] flex justify-center px-4">
      <div class="pointer-events-auto flex items-center gap-2.5 rounded px-2.5 py-2 backdrop-blur bg-talos-bg/95"
        style="border:1px solid rgb(var(--talos-accent-rgb) / 0.22);box-shadow:0 14px 40px rgb(var(--talos-accent-rgb) / 0.12)">
        <app-button variant="ghost" [icon]="CircleQuestionMarkIcon" [iconOnly]="true" size="md" [tooltip]="'PROJECT_STEPPER.NAV.RESTART_TOUR' | translate" (clicked)="tour.restart()" />

        @if (stepper.currentStep() > 1) {
          <app-button variant="secondary" [icon]="ChevronLeftIcon" [iconOnly]="true" size="md" [label]="'PROJECT_STEPPER.NAV.PREVIOUS' | translate" [tooltip]="'PROJECT_STEPPER.NAV.PREVIOUS' | translate" (clicked)="onBack()" />
        } @else {
          <div class="flex h-9 items-center gap-2 rounded px-3 text-xs text-talos-accent" style="background:rgb(var(--talos-accent-rgb) / 0.07)">
            <lucide-icon [img]="LayersIcon" [size]="14" />
            <span class="font-semibold">
              {{ stepper.selectedCategories().length }}/{{ stepper.maxDetectionScopeCategories }}
            </span>
          </div>
        }

        <div class="flex items-center gap-2 px-1" role="list" [attr.aria-label]="'PROJECT_STEPPER.ARIA.WORKFLOW_STEPS' | translate">
          @for (step of stepper.steps; track step.number) {
            <div
              role="listitem"
              class="flex items-center justify-center rounded-full transition-all duration-200"
              [attr.aria-label]="stepper.stepLabel(step.number) + (stepper.currentStep() > step.number ? ' - ' + ('PROJECT_STEPPER.ARIA.COMPLETE' | translate) : stepper.currentStep() === step.number ? ' - ' + ('PROJECT_STEPPER.ARIA.CURRENT' | translate) : ' - ' + ('PROJECT_STEPPER.ARIA.UPCOMING' | translate))"
              [attr.aria-current]="stepper.currentStep() === step.number ? 'step' : null"
              [ngClass]="
                stepper.currentStep() > step.number
                  ? 'h-3.5 w-3.5 bg-talos-accent text-[var(--talos-bg)]'
                  : stepper.currentStep() === step.number
                    ? 'h-3.5 w-8 rounded-full bg-talos-ink'
                    : 'h-2.5 w-2.5 bg-talos-ink/20'
              "
            >
              @if (stepper.currentStep() > step.number) {
                <lucide-icon [img]="CheckIcon" [size]="9" />
              }
            </div>
          }
        </div>

        <div class="min-w-[3.25rem] text-center">
          <p class="text-[10px] font-semibold uppercase tracking-[0.18em]" style="color:rgb(var(--talos-ink-rgb) / 0.4)">{{ 'PROJECT_STEPPER.HEADER.STEP' | translate }}</p>
          <p class="text-sm font-semibold text-talos-ink">{{ stepper.currentStep() }}/{{ stepper.steps.length }}</p>
        </div>

        <app-button
          variant="primary"
          [icon]="stepper.currentStep() === stepper.steps.length ? CheckIcon : ChevronRightIcon"
          [iconOnly]="true"
          size="md"
          [loading]="stepper.detectionScopeSaving() || pricingDrawerSaving() || stepper.hydrationLoading()"
          [disabled]="!canContinue() || stepper.detectionScopeLoading() || stepper.detectionScopeSaving() || pricingDrawerSaving() || stepper.hydrationLoading()"
          [label]="continueLabel()"
          [tooltip]="continueTooltip()"
          (clicked)="onContinue()"
        />
      </div>
    </div>
  </div>
}
